import { UtilityType } from "@/api/facilities";
import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetFacilityImpactTotals extends Result {
  result: {
    utility_impact: IImpact;
    company_impact: IImpact;
  };
}

export function useFacilityImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "facility", "facilityImpactTotals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetFacilityImpactTotals>("/dashboard/facilities/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

type GetFacilityImpactPerType = {
  name: string;
  amount: number;
  impact: IImpact;
}[];

export function useFacilityImpactPerType({ metric }: { metric: ImpactMetricKey }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "facility", "facilityImpactPerType", startDate, endDate, metric],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetFacilityImpactPerType>("/dashboard/facilities/per_type", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export interface IFacilityImpactSource {
  type: string;
  utility_source_id: number;
  utility_source_label: string;
  amount: number;
  unit: string;
  facilities: { id: number; name: string }[];
  impact: IImpact;
}

interface GetFacilityImpactSources {
  total_impact: IImpact;
  total_rows: number;
  sources: IFacilityImpactSource[];
}

export type SourceGroupBy = "id" | "name";

export function useFacilityImpactPerSource({
  pagination,
  filter,
  search,
  sort,
  groupBy,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  groupBy: SourceGroupBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "facility",
      "facilityImpactSources",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      groupBy,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetFacilityImpactSources>("/dashboard/facilities/per_source", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            group_by: groupBy,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export function useFacilityImpactPerSourceExport({
  pagination,
  filter,
  search,
  sort,
  groupBy,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  groupBy: SourceGroupBy;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/facilities/per_source", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            group_by: groupBy,
          },
        })
        .then((result) => result.data.result.url),
  });
}

export interface ImpactPerFacility {
  id: string;
  name: string;
  impact: Record<UtilityType | "total", IImpact>;
}

export interface GetFacilityImpactPerFacility extends Result {
  result: {
    total_impact: IImpact;
    total_rows: number;
    facilities: ImpactPerFacility[];
  };
}

export function useFacilityImpactPerFacility({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "facility",
      "facilityImpactPerFacility",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetFacilityImpactPerFacility>("/dashboard/facilities/facilities", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export function useFacilityImpactPerFacilityExport({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/facilities/facilities", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}
